import React, { useEffect, useMemo, useState } from "react";
import GridTable from "cvmgmt-cube-grid-table";
import { ColumnFilter, SortingState } from "@tanstack/react-table";
import { Pagination } from "cprime-foundational-components";

const IneligibleSelections = ({ errorData }: { errorData: any }) => {
  const [filters, setFilters] = useState<ColumnFilter[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 25,
  });

  // Function to apply filtering, sorting, and pagination
  const applyFiltersAndSorting = (rows: any[]) => {
    let filteredRows = [...rows];

    // Apply filters
    filters.forEach((filter) => {
      const { id, value } = filter;
      filteredRows = filteredRows.filter((row) =>
        row[id]?.toLowerCase().includes((value as string).toLowerCase()),
      );
    });

    // Apply sorting
    sorting.forEach(({ id, desc }) => {
      filteredRows.sort((a, b) => {
        const valA = a[id] || "";
        const valB = b[id] || "";
        if (valA < valB) return desc ? 1 : -1;
        if (valA > valB) return desc ? -1 : 1;
        return 0;
      });
    });

    // Apply pagination
    const start = pagination?.page * pagination?.rowsPerPage;
    const end = start + pagination?.rowsPerPage;
    const paginatedRows = filteredRows?.slice(start, end);

    return {
      rows: paginatedRows,
      totalCount: filteredRows?.length,
    };
  };

  // Memoized grid data to ensure performance optimization
  const gridData = useMemo(() => {
    const rows = errorData?.rows || [];

    // Apply filters, sorting, and pagination
    const { rows: paginatedRows, totalCount } = applyFiltersAndSorting(rows);

    return {
      columns:
        errorData?.columns?.map((col: any) => ({
          id: col.id,
          name: col.name,
          type: col.type || "DEFAULT",
          size: col?.size ?? 200,
          minSize: col?.minSize ?? 200,
        })) || [],
      rows:
        paginatedRows.map((row: any) => ({
          user_email: row?.user_email,
          feature_group: row?.feature_group,
          feature_name: row?.feature_name,
          permission_name: row?.permission_name,
          error_message: row?.error_message,
        })) || [],
      totalCount: totalCount || 0,
    };
  }, [errorData, pagination, filters, sorting]);

  return (
    <div>
      <p>{`There are ${errorData?.invalidCount} selection(s) that are ineligible since access to feature and permission has already been requested. Would you like to proceed with ${errorData?.validCount} eligible selection(s)?`}</p>

      {/* GridTable Component */}
      <GridTable
        data={gridData}
        customPayload={true}
        isPagination={true}
        isCustomizeColumns={false}
        isSorting={true}
        isFiltering={true}
        title={""}
        filters={filters}
        sorting={sorting}
        setSorting={setSorting}
        setFilters={setFilters}
        showLoader={false}
      />

      {/* Pagination Component */}
      <Pagination
        sx={{ width: "100%" }}
        {...{
          ...pagination,
          count: gridData?.totalCount ?? 0,
          rowsPerPageOptions: [25, 50, 100, 250],
          onPageChange: (e: any, number: any) => {
            setPagination({ ...pagination, page: number });
          },
          onRowsPerPageChange: (evt: { target: { value: any } }) => {
            setPagination({
              page: 0,
              rowsPerPage: Number(evt?.target?.value),
            });
          },
        }}
      />
    </div>
  );
};

export default IneligibleSelections;
